/* eslint-disable no-param-reassign */
import { ActionTree } from 'vuex';
import axios, { AxiosResponse } from 'axios';
import {
	Option,
	GetOffersResponse,
	GetLocationsResponse,
	GetLocationsResponseKr,
	GetLocationsResponseTgt,
	getOffers,
	getLocations,
	MercuryRetailer,
	integratedRetailerMap,
} from '@andromeda/polaris-data-access-products';
import { Location } from '@/interfaces/Location';
import { Retailer } from '@/interfaces/Retailer';
import { queryString } from '@/utils';
import { Takeover } from '@/takeover';
import MxrRetailer from '@/interfaces/MxrRetailer';
import RetailerOffer from '@/interfaces/RetailerOffer';
import { Partner } from '@/interfaces/Partner';
import { IMxrConfig } from '@/interfaces/MxrConfig';
import { ProductShoppingListItem } from '@/interfaces/ProductShoppingListItem';
import { Tab } from '@/interfaces/Tab';
import {
	CLOSE_SWAP_MODAL,
	FetchIntegratedRetailerLocationsPaylod,
	FetchMxrRetailerLocationsPaylod,
	FetchMxrRetailerOffersPayload,
	FETCH_MXR_RETAILER_LOCATIONS,
	GET_FULFILLMENT_STORE,
	GET_MERCURY_LOCATION_RESULT,
	GetPromotionsPayload,
	NEXT_CARD,
	OPEN_MXR_SWAP_MODAL,
	PREV_CARD,
	SELECT_TAB,
	SET_ACTIVE_CARD,
	SET_CAROUSEL_PROMOTIONS,
	SET_CURRENT_MXR_RETAILER_INDEX,
	SET_FULFILLMENT_STORE,
	SET_GO_TO_CART_OPEN,
	SET_INTEGRATED_RETAILER_LOCATIONS,
	SET_IS_DEFAULT,
	SET_LOADING_PROMOTIONS,
	SET_MERCURY_LOCATION_RESULT,
	SET_MXR_CONFIG,
	SET_MXR_PROMOTIONS,
	SET_MXR_RETAILER,
	SET_MXR_RETAILER_LOCATIONS,
	SET_MXR_RETAILER_OFFERS,
	SET_PROMOTION_CAROUSELS,
	SET_RETAILER,
	SET_SELECTED_SWAP,
	SetMxrRetailerOffersPayload,
	TOGGLE_RETAILER_LOCATIONS_MODAL,
	UPDATE_PRODUCT_SHOPPING_LIST_ITEM,
	UPDATE_SWAP_OPTIONS,
	UPDATE_VISIBLE_INGREDIENTS,
	UPDATE_VISIBLE_PROMOTION_IDS,
	SET_MXR_OFFERS,
	GET_ALL_BASE_INGREDIENTS_AND_MAX_OFFERS,
	SET_CHECKOUT_TOTAL,
	SET_SELECTED_TAB_INDEX,
	SET_PREVIOUS_PROMOTION,
	SET_OFFERS_COUNT_IN_CART,
} from '../types';
import { IRootState } from './state';
import { PromotionCarouselItem } from '../../interfaces/PromotionCarouselItem';
import { IPromotion } from '@/interfaces/Promotion';
import { retailerLogos } from '@/utils/retailerLogo';

/**
 * Calls Mercury API to get retailer offers for Takover retailer
 * @param takeover Retailer Cart Takeover
 * @param fulfillmentStoreId Store Id to find offers for
 */
const fetchMxrRetailerOffers = async (
	takeover: Takeover,
	fulfillmentStoreId: string
): Promise<RetailerOffer[] | undefined> => {
	try {
		const limitp = queryString.getMax();
		const limit = parseInt(limitp, 10);
		const retailer =
			takeover.Retailer === Partner.WALMART
				? MercuryRetailer.WALMART
				: MercuryRetailer.KROGER;
		// eslint-disable-next-line no-restricted-globals
		const offerResult: Option<GetOffersResponse> = isNaN(limit)
			? await getOffers(
					takeover.ItemIds,
					retailer,
					fulfillmentStoreId,
					false,
					limit,
					false
			  )
			: await getOffers(
					takeover.ItemIds,
					retailer,
					fulfillmentStoreId,
					false,
					limit,
					false
			  );
		if (!offerResult.some) {
			console.error(offerResult.reason);
			return [];
		}
		return offerResult.some.offers.map(RetailerOffer.FromMercuryOffer);
	} catch (err) {
		throw err;
	}
};

const fetchMxrOffers = async (
	retailer: MercuryRetailer,
	storeId: string,
	baseIngItemIds: number[],
	maxOffers: number = 5,
	host: string = 'https://live.polaris.me'
): Promise<RetailerOffer[] | undefined> => {
	try {
		const offerResult: Option<GetOffersResponse> = await getOffers(
			baseIngItemIds,
			retailer,
			storeId,
			false,
			maxOffers,
			false,
			host
		);
		if (!offerResult.some) {
			console.error(offerResult.reason);
			return [];
		}
		return offerResult.some.offers.map(RetailerOffer.FromMercuryOffer);
	} catch (err) {
		throw err;
	}
};

export const actions: ActionTree<IRootState, IRootState> = {
	[SET_LOADING_PROMOTIONS]: async (
		{ state, commit, getters },
		payload: GetPromotionsPayload
	): Promise<void> => {
		const { takeover, fulfillmentStoreId } = { ...payload };
		try {
			commit(SET_LOADING_PROMOTIONS, true);
			let zipCode = queryString.getZipCode() || window.sm_params.zipCode;
			if (zipCode && zipCode.length) {
				zipCode = `?zipCode=${zipCode}`;
			} else {
				zipCode = '';
			}
			if (!state.config.id || !state.config.apiHost) {
				return;
			}
			const resp: AxiosResponse = await axios.get(
				`https://${state.config.apiHost}/v1/mxr/${state.config.id}.json${zipCode}`
			);
			if (!resp.data) return;

			if (resp.data.carousels && resp.data.carousels.length) {
				const carousels = resp.data.carousels.map((c: any) => {
					const pci = new PromotionCarouselItem(
						c.id,
						c.heading,
						[],
						[],
						c.baseIngredientId
					);
					pci.setDefaultPromotions(c.promotions);
					return pci;
				});
				commit(SET_PROMOTION_CAROUSELS, carousels);
			}

			if (takeover) {
				let offers: RetailerOffer[] | undefined = [];
				const bimo = getters[GET_ALL_BASE_INGREDIENTS_AND_MAX_OFFERS];
				await Promise.all(
					Object.keys(bimo).map(async (k) => {
						const promotions = await fetchMxrOffers(
							payload.takeover.MercuryRetailer,
							fulfillmentStoreId,
							bimo[k],
							+k,
							state.config.host ? state.config.host : 'https://live.polaris.me'
						);
						offers = offers!.concat(promotions!);
					})
				);

				if (offers.length > 0) {
					commit(SET_MXR_RETAILER_OFFERS, {
						offers,
						fulfillmentStoreId,
					} as SetMxrRetailerOffersPayload);
				}
			} else {
				const retailerLoc =
					resp.data.retailer && resp.data.retailer.location
						? (resp.data.retailer.location as Location)
						: new Location('', '', '', '', '');
				// Currently the retailer is being used to show a Brand logo if the MXR card is sponosored by a Brand (Hidden Valley)
				// Until POL-375 is done we'll have to live with checking for existing values and not replacing them
				const retailer = new Retailer(
					state.config.retailer.id
						? state.config.retailer.id
						: resp.data.retailer.id,
					state.config.retailer.name.length
						? state.config.retailer.name
						: resp.data.retailer.name,
					state.config.retailer.logo
						? state.config.retailer.logo
						: resp.data.retailer.logo,
					retailerLoc
				);
				commit(SET_RETAILER, retailer);
				state.config.promotions.forEach((p) => {
					p.promotions = p.defaultPromotions;
				});
			}
			return;
		} catch (err) {
			throw err;
		} finally {
			commit(SET_LOADING_PROMOTIONS, false);
		}
	},
	[SET_MXR_PROMOTIONS]: async (
		{ state, commit },
		payload: { zipcode: string }
	): Promise<void> => {
		try {
			const { zipcode } = { ...payload };
			commit(SET_LOADING_PROMOTIONS, true);
			if (!state.config.id || !state.config.apiHost) {
				return;
			}
			const resp: AxiosResponse = await axios.get(
				`https://${state.config.apiHost}/v1/mxr/${state.config.id}.json${zipcode}`
			);
			if (!resp.data) return;

			if (resp.data.carousels && resp.data.carousels.length) {
				const carousels = resp.data.carousels.map((c: any) => {
					const pci = new PromotionCarouselItem(
						c.id,
						c.heading,
						[],
						[],
						c.baseIngredientId
					);
					pci.setDefaultPromotions(c.promotions);
					return pci;
				});
				commit(SET_PROMOTION_CAROUSELS, carousels);
			}
			return;
		} catch (err) {
			throw err;
		} finally {
			commit(SET_LOADING_PROMOTIONS, false);
		}
	},
	[SET_MXR_OFFERS]: async (
		{ commit, getters },
		payload: { takeover: Takeover; promotion: IMxrConfig }
	): Promise<void> => {
		try {
			const locationId = getters[GET_FULFILLMENT_STORE]
				? getters[GET_FULFILLMENT_STORE].LocationId
				: undefined;

			let offers: RetailerOffer[] | undefined = [];
			const bimo = getters[GET_ALL_BASE_INGREDIENTS_AND_MAX_OFFERS];
			await Promise.all(
				Object.keys(bimo).map(async (k) => {
					const promotions = await fetchMxrOffers(
						payload.takeover.MercuryRetailer,
						locationId,
						bimo[k],
						+k
					);
					offers = offers!.concat(promotions!);
				})
			);

			if (offers.length > 0) {
				commit(SET_MXR_RETAILER_OFFERS, {
					offers,
					fulfillmentStoreId: locationId,
				} as SetMxrRetailerOffersPayload);
			}
			return;
		} catch (err) {
			throw err;
		}
	},
	[FETCH_MXR_RETAILER_LOCATIONS]: async (
		{ commit },
		payload: FetchMxrRetailerLocationsPaylod
	): Promise<void> => {
		try {
			const { zip, max, takeover, host } = { ...payload };
			let locations: MxrRetailer[] = [];
			const retailer =
				takeover.Retailer === Partner.WALMART
					? MercuryRetailer.WALMART
					: MercuryRetailer.KROGER;
			const locResult: Option<
				| GetLocationsResponse
				| GetLocationsResponseKr
				| GetLocationsResponseTgt
			> = await getLocations(retailer, zip, max, host);
			if (!locResult.some) {
				console.debug(locResult.reason);
			} else {
				locations =
					takeover.Retailer === Partner.WALMART
						? MxrRetailer.FromMercuryGetLocationsResponse(
								locResult.some as GetLocationsResponse,
								takeover
						  )
						: MxrRetailer.FromMercuryGetLocationsResponseKr(
								locResult.some as GetLocationsResponseKr,
								takeover
						  );
			}
			commit(SET_MXR_RETAILER_LOCATIONS, locations);
			if (locations.length === 0) {
				commit(SET_IS_DEFAULT, true);
			}
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_INTEGRATED_RETAILER_LOCATIONS]: async (
		{ commit },
		payload: FetchIntegratedRetailerLocationsPaylod
	): Promise<void> => {
		try {
			// let locations: MxrRetailer[] = [];
			const retailer: MercuryRetailer =
				integratedRetailerMap[payload.promotion.integratedRetailer];
			payload.max = isNaN(payload.max) ? 50 : payload.max;
			const locResult: Option<
				| GetLocationsResponse
				| GetLocationsResponseKr
				| GetLocationsResponseTgt
			> = await getLocations(retailer, payload.zip, payload.max, payload.promotion.host);
			if (!locResult.some) {
				console.debug(locResult.reason);
			}
			commit(SET_MERCURY_LOCATION_RESULT, locResult.some);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_MXR_RETAILER_LOCATIONS]: async (
		{ commit, getters },
		payload: FetchIntegratedRetailerLocationsPaylod
	): Promise<void> => {
		try {
			let locations: MxrRetailer[] = [];
			locations = MxrRetailer.getLocationsResponse(
				getters[GET_MERCURY_LOCATION_RESULT] as GetLocationsResponse,
				payload.takeover,
				payload.promotion.integratedRetailer
			);
			commit(SET_MXR_RETAILER_LOCATIONS, locations);
			if (locations.length === 0) {
				commit(SET_IS_DEFAULT, true);
				return;
			}
			commit(SET_FULFILLMENT_STORE, locations.some[0]);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_MXR_RETAILER_OFFERS]: async (
		{ commit },
		payload: FetchMxrRetailerOffersPayload
	): Promise<void> => {
		try {
			const offers = await fetchMxrRetailerOffers(
				payload.takeover,
				payload.fulfillmentStoreId
			);
			commit(SET_MXR_RETAILER_OFFERS, {
				offers,
				fulfillmentStoreId: payload.fulfillmentStoreId,
			} as SetMxrRetailerOffersPayload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_MXR_RETAILER]: async (
		{ state, commit, dispatch },
		location: MxrRetailer
	): Promise<void> => {
		try {
			let mrIndex = -1;
			for (let i = 0; i < state.mxrRetailerLocations.length; i += 1) {
				if (
					state.mxrRetailerLocations[i].FulfillmentStoreId ===
					location.FulfillmentStoreId
				) {
					mrIndex = i;
					break;
				}
			}

			if (mrIndex === -1) return;

			commit(SET_CURRENT_MXR_RETAILER_INDEX, mrIndex);
			dispatch(SET_LOADING_PROMOTIONS, {
				takeover: state.takeover,
				fulfillmentStoreId: location.LocationId,
			});
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_CAROUSEL_PROMOTIONS]: async (
		{ commit },
		payload: PromotionCarouselItem[]
	): Promise<void> => {
		try {
			commit(SET_CAROUSEL_PROMOTIONS, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_MXR_CONFIG]: async (
		{ commit },
		payload: IMxrConfig
	): Promise<void> => {
		try {
			commit(SET_MXR_CONFIG, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_ACTIVE_CARD]: async ({ commit }, payload: number): Promise<void> => {
		try {
			commit(SET_ACTIVE_CARD, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_SELECTED_SWAP]: async (
		{ commit },
		payload: ProductShoppingListItem
	): Promise<void> => {
		try {
			commit(SET_SELECTED_SWAP, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SELECT_TAB]: async ({ commit }, payload: Tab): Promise<void> => {
		try {
			commit(SELECT_TAB, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_SELECTED_TAB_INDEX]: async (
		{ commit },
		payload: number
	): Promise<void> => {
		try {
			commit(SET_SELECTED_TAB_INDEX, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_GO_TO_CART_OPEN]: async (
		{ commit },
		payload: boolean
	): Promise<void> => {
		try {
			commit(SET_GO_TO_CART_OPEN, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[NEXT_CARD]: async ({ commit }): Promise<void> => {
		try {
			commit(NEXT_CARD);
			return;
		} catch (err) {
			throw err;
		}
	},
	[PREV_CARD]: async ({ commit }): Promise<void> => {
		try {
			commit(PREV_CARD);
			return;
		} catch (err) {
			throw err;
		}
	},
	[TOGGLE_RETAILER_LOCATIONS_MODAL]: async ({ commit }): Promise<void> => {
		try {
			commit(TOGGLE_RETAILER_LOCATIONS_MODAL);
			return;
		} catch (err) {
			throw err;
		}
	},
	[UPDATE_VISIBLE_PROMOTION_IDS]: async (
		{ commit },
		payload
	): Promise<void> => {
		try {
			commit(UPDATE_VISIBLE_PROMOTION_IDS, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[UPDATE_PRODUCT_SHOPPING_LIST_ITEM]: async (
		{ commit },
		payload: ProductShoppingListItem
	): Promise<void> => {
		try {
			commit(UPDATE_PRODUCT_SHOPPING_LIST_ITEM, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[OPEN_MXR_SWAP_MODAL]: async (
		{ commit },
		payload: { item: ProductShoppingListItem; swapSection: number }
	): Promise<void> => {
		try {
			commit(OPEN_MXR_SWAP_MODAL, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[UPDATE_SWAP_OPTIONS]: async (
		{ commit },
		payload: ProductShoppingListItem
	): Promise<void> => {
		try {
			commit(UPDATE_SWAP_OPTIONS, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[CLOSE_SWAP_MODAL]: async ({ commit }): Promise<void> => {
		try {
			commit(CLOSE_SWAP_MODAL);
			return;
		} catch (err) {
			throw err;
		}
	},
	[UPDATE_VISIBLE_INGREDIENTS]: async (
		{ commit },
		payload: ProductShoppingListItem
	): Promise<void> => {
		try {
			commit(UPDATE_VISIBLE_INGREDIENTS, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_CHECKOUT_TOTAL]: async (
		{ commit },
		payload: number
	): Promise<void> => {
		try {
			commit(SET_CHECKOUT_TOTAL, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_PREVIOUS_PROMOTION]: async (
		{ commit },
		payload: IPromotion | null
	): Promise<void> => {
		try {
			commit(SET_PREVIOUS_PROMOTION, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_OFFERS_COUNT_IN_CART]: async (
		{ commit },
		payload: number
	): Promise<void> => {
		try {
			commit(SET_OFFERS_COUNT_IN_CART, payload);
			return;
		} catch (err) {
			throw err;
		}
	},
	[SET_RETAILER]: async ({ state, commit }): Promise<void> => {
		// This action is a bandaid for the MXR Live Preview in polaris-console.
		// The shopper-gam retailer is not updated until you save
		// your work in polaris-console, so it can get out of sync with the
		// preview and produce weird visual results.
		// This retailerLogos util enables bypassing shopper-gam and previewing
		// the image and name in realtime.
		const retailer = retailerLogos[state.config.retailer.id];

		if (!retailer) return;

		commit(
			SET_RETAILER,
			new Retailer(
				retailer.id,
				retailer.name,
				retailer.imageUrl,
				state.config.retailer.location
			)
		);
	},
};
